import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAP } from "../lib/nap";

const LINKS = [
  { to: "/", label: "Home" },
  {
    to: "/chi-siamo",
    label: "Chi Siamo",
    children: [
      { to: "/chi-siamo", label: "Chi Siamo" },
      { to: "/chi-siamo/storia", label: "La nostra Storia" },
    ],
  },
  {
    to: "/servizi",
    label: "Servizi",
    children: [
      { to: "/servizi", label: "Servizi" },
      { to: "/servizi/listino", label: "Listino prezzi" },
    ],
  },
  { to: "/staff", label: "Staff" },
  { to: "/galleria", label: "Galleria" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubMobile, setOpenSubMobile] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOpenSubMobile(null); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-[#C9A961]/15 py-1" : "bg-transparent py-2"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/assets/logo-palazzolo.png" alt="Beauty Salon by Itria Leone" className="h-11 w-auto object-contain" style={{ filter: "drop-shadow(0 0 6px rgba(201,169,97,0.2))" }} />
          <div className="leading-none hidden sm:block">
            <div className="font-display text-[16px] tracking-[0.18em] uppercase text-white">{NAP.displayName}</div>
            <div className="font-script text-[14px] text-[#C9A961] -mt-1">{NAP.byLine} · {NAP.city}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => {
            const active = location.pathname === l.to || (l.children && l.children.some((c) => c.to === location.pathname));
            const cls = `relative text-[12px] uppercase tracking-[0.22em] transition-colors duration-300 ${active ? "text-[#C9A961]" : "text-white/80 hover:text-[#C9A961]"}`;
            const testId = `nav-${l.label.toLowerCase().replace(/\s/g, "-")}`;
            if (!l.children) {
              return (
                <NavLink key={l.to} to={l.to} data-testid={testId} className={cls}>
                  {l.label}
                  {active && <span className="absolute -bottom-2 left-0 right-0 h-px bg-[#C9A961]" />}
                </NavLink>
              );
            }
            return (
              <div key={l.to} className="relative group">
                <Link to={l.to} data-testid={testId} className={`${cls} inline-flex items-center gap-1.5`}>
                  {l.label}
                  <ChevronDown size={12} className="opacity-60 group-hover:opacity-100" />
                  {active && <span className="absolute -bottom-2 left-0 right-0 h-px bg-[#C9A961]" />}
                </Link>
                <div className="absolute top-full left-0 mt-3 min-w-[220px] bg-[#0a0a0a]/95 backdrop-blur-xl border border-[#C9A961]/20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                  {l.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={({ isActive }) => `block px-5 py-3 text-[11px] uppercase tracking-[0.22em] border-b border-white/5 last:border-b-0 transition-colors ${isActive ? "text-[#C9A961] bg-[#C9A961]/5" : "text-white/75 hover:text-[#C9A961] hover:bg-white/5"}`}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <Link
          to="/prenotazioni"
          data-testid="nav-booking-btn"
          className="hidden lg:inline-flex items-center gap-2 border border-[#C9A961] text-[#C9A961] px-6 py-3 text-[11px] uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-[#0a0a0a] transition-all duration-300"
        >
          Prenota
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-[#C9A961]/20" data-testid="mobile-menu">
          <nav className="flex flex-col py-4">
            {LINKS.map((l) => {
              if (!l.children) {
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) => `px-8 py-4 text-sm uppercase tracking-[0.22em] border-b border-white/5 ${isActive ? "text-[#C9A961]" : "text-white/80"}`}
                  >
                    {l.label}
                  </NavLink>
                );
              }
              const sub = openSubMobile === l.to;
              return (
                <div key={l.to} className="border-b border-white/5">
                  <button
                    data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}-toggle`}
                    onClick={() => setOpenSubMobile(sub ? null : l.to)}
                    className="w-full px-8 py-4 text-sm uppercase tracking-[0.22em] text-white/80 flex items-center justify-between"
                  >
                    {l.label} <ChevronDown size={14} className={`transition-transform ${sub ? "rotate-180" : ""}`} />
                  </button>
                  {sub && (
                    <div className="bg-white/5 pb-2">
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`mnav-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                          className={({ isActive }) => `block px-12 py-3 text-[11px] uppercase tracking-[0.22em] ${isActive ? "text-[#C9A961]" : "text-white/70"}`}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/prenotazioni"
              data-testid="mnav-booking"
              className="mx-8 mt-4 border border-[#C9A961] text-[#C9A961] text-center py-4 text-sm uppercase tracking-[0.22em]"
            >
              Prenota ora
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
